import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import type { TaskSubtask } from '@/types/projects';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export type SubtaskDraft = Pick<TaskSubtask, 'id' | 'title' | 'is_done'>;

interface Props {
  subtasks: SubtaskDraft[];
  onChange: (subtasks: SubtaskDraft[]) => void;
}

export const TaskSubtaskEditor: React.FC<Props> = ({ subtasks, onChange }) => {
  const { t } = useTranslation();
  const [newTitle, setNewTitle] = useState('');

  const addSubtask = () => {
    const title = newTitle.trim();
    if (!title) return;
    onChange([...subtasks, { title, is_done: false }]);
    setNewTitle('');
  };

  const toggleDone = (index: number) => {
    onChange(subtasks.map((item, i) => (
      i === index ? { ...item, is_done: !item.is_done } : item
    )));
  };

  const updateTitle = (index: number, title: string) => {
    onChange(subtasks.map((item, i) => (i === index ? { ...item, title } : item)));
  };

  const removeSubtask = (index: number) => {
    onChange(subtasks.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{t('projects.subtasks')}</label>
      {subtasks.length > 0 && (
        <ul className="space-y-1 mb-2">
          {subtasks.map((item, index) => (
            <li key={item.id ?? `new-${index}`} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.is_done}
                onChange={() => toggleDone(index)}
                className="rounded border-border"
              />
              <input
                value={item.title}
                onChange={(e) => updateTitle(index, e.target.value)}
                className={`flex-1 px-2 py-1 border border-border rounded bg-bgBase text-sm text-textDefault ${
                  item.is_done ? 'line-through text-textSecondary' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => removeSubtask(index)}
                className="p-1 text-textSecondary hover:text-red-600"
                aria-label={t('common.delete')}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addSubtask();
            }
          }}
          placeholder={t('projects.subtaskPlaceholder')}
          className="flex-1 px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault text-sm"
        />
        <button
          type="button"
          onClick={addSubtask}
          disabled={!newTitle.trim()}
          className="px-3 py-2 border border-border rounded-lg text-sm hover:bg-bgElevated disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
